import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { effectStrings } from "./constants";
import HelperText from "../../../../../components/atoms/HelperText";
import { colorPathogenicity } from "../helpers";
import { Pagination } from "../../../../../components/atoms";

const PathogenicitiesTable = ({ pathogenicities }) => {
  const [newPathogenicities, setNewPathogenicities] = useState(pathogenicities);

  const filterBySearch = (event) => {
    const query = event.target.value;
    if (query.length > 0) {
      let updatedList = [...pathogenicities];

      updatedList = updatedList.filter((item) =>
        item.clinical_classification_submitter
          ?.toLowerCase()
          .includes(query.toLowerCase())
      );
      updatedList.sort((a, b) => {
        const aValue = a.clinical_classification_submitter.toLowerCase();
        const bValue = b.clinical_classification_submitter.toLowerCase();

        if (aValue === query && bValue !== query) return -1;
        if (bValue === query && aValue !== query) return 1;
        if (aValue.startsWith(query) && !bValue.startsWith(query.toLowerCase()))
          return -1;
        if (!aValue.startsWith(query) && bValue.startsWith(query.toLowerCase()))
          return 1;

        return 0;
      });
      setNewPathogenicities(updatedList);
    } else {
      setNewPathogenicities(pathogenicities);
    }
  };

  const totalPathogenicities = newPathogenicities?.length || 1;
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10];
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [
    setPage,
    pageSize,
    pathogenicities,
    totalPathogenicities,
    newPathogenicities,
  ]);

  if (pathogenicities && pathogenicities.length > 0) {
    const title =
      pathogenicities.length === 1
        ? "Classification submission"
        : "Classification submissions";
    const text =
      "Data includes shared variant classifications from submitter (diagnostic centers or consortia) or from LOVD curators summary records. These entries do not include patient information.";

    return (
      <div
        className="varsome-inner-container tw-overflow-x-auto"
        data-testid="pathogenicities-table"
      >
        <h6>
          {title} <HelperText customText={text} />
        </h6>

        <div>
          Filter by Clinical Classification:&nbsp;
          <span>
            <input
              onChange={filterBySearch}
              className="tw-h-8 tw-text-blueGray-500 tw-px-2 tw-w-32 tw-border tw-border-blueGray-300"
              placeholder="e.g. Likely Benign"
              autoComplete="on"
              type="text"
            />
          </span>
        </div>

        <div className="tw-flex tw-items-center tw-justify-center">
          <div className="tw-text-2xs">
            <Pagination
              currentPage={page}
              totalResults={totalPathogenicities}
              pageSize={pageSize}
              onPageChange={setPage}
              pageSizeOptions={pageSizeOptions}
              onPageSizeChange={setPageSize}
            />
          </div>
        </div>
        <table className="varsome-table">
          <thead>
            <tr>
              <th className="tw-w-1/4 tw-font-semibold">
                Clinical Classification
              </th>
              <th className="tw-w-1/4 tw-font-semibold">Source</th>
              <th className="tw-w-1/4 tw-font-semibold">Effect Curator</th>
              <th className="tw-w-1/4 tw-font-semibold">Effect Submitter</th>
            </tr>
          </thead>
          <tbody data-testid="pathogenicities-table-body">
            {newPathogenicities
              .slice(pageSize * (page - 1), pageSize * page)
              .map((pathogenicity, i) => {
                return (
                  <tr key={`${i}-${new Date().getTime()}`}>
                    {pathogenicity?.clinical_classification_curator && (
                      <td data-testid="pathogenicities-table-classification-curator">
                        {colorPathogenicity(
                          pathogenicity?.clinical_classification_curator
                        )}
                        <br />
                      </td>
                    )}
                    {pathogenicity?.clinical_classification_submitter && (
                      <td data-testid="pathogenicities-table-classification-submitter">
                        {colorPathogenicity(
                          pathogenicity?.clinical_classification_submitter
                        )}
                      </td>
                    )}
                    {!pathogenicity?.clinical_classification_curator &&
                      !pathogenicity?.clinical_classification_submitter && (
                        <td data-testid="pathogenicities-table-classification">
                          -
                        </td>
                      )}
                    <td data-testid="pathogenicities-table-classification-source">
                      {pathogenicity?.clinical_classification_curator &&
                        "Curator"}
                      {pathogenicity?.clinical_classification_submitter &&
                        "Submitter"}
                      {!pathogenicity?.clinical_classification_curator &&
                        !pathogenicity?.clinical_classification_submitter &&
                        "-"}
                      {(pathogenicity?.clinical_classification_curator ||
                        pathogenicity?.clinical_classification_submitter) &&
                        pathogenicity?.lovd_link && (
                          <>
                            {" - "}
                            <small>
                              <a
                                href={pathogenicity?.lovd_link}
                                target="_blank"
                              >
                                {` See in LOVD`}
                              </a>
                            </small>
                          </>
                        )}
                    </td>
                    <td data-testid="pathogenicities-table-effect-curator">
                      {pathogenicity?.effect_curator
                        ? effectStrings[pathogenicity?.effect_curator] ||
                          pathogenicity?.effect_curator
                        : "-"}
                    </td>
                    <td data-testid="pathogenicities-table-effect-submitter">
                      {pathogenicity?.effect_submitter
                        ? effectStrings[pathogenicity?.effect_submitter] ||
                          pathogenicity?.effect_submitter
                        : "-"}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

PathogenicitiesTable.propTypes = {
  pathogenicities: PropTypes.arrayOf(
    PropTypes.shape({
      clinical_classification_submitter: PropTypes.string,
      clinical_classification_curator: PropTypes.string,
      effect_submitter: PropTypes.string,
      effect_curator: PropTypes.string,
      effect_symbol: PropTypes.string,
    })
  ),
};

PathogenicitiesTable.defaultProps = {
  pathogenicities: [],
};

export default PathogenicitiesTable;
